import { type ApiProblem, call, newIdempotencyKey } from '@rental/contracts/client'
import { format, formatEuro } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQueryClient } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useId, useRef, useState } from 'react'
import { useAccess } from '../access-context'
import { api } from '../api'
import { ReasonList, SubPanel } from '../components/SubPanel'
import { FIELD, FIELD_ROW, NamedFor, ProblemNotice, StaffInput } from '../components/ui'
import { noImplicitSubmit } from '../forms'
import { useLocale } from '../i18n'
import { parseEuroCents } from '../money'
import { type CashDrawer, openingShort } from './drawer'
import { CASH_QUERY_KEY, MAX_COUNT_CENTS } from './queries'

/**
 * S5 (PAY-6): opens a pick-up point's drawer with what is counted in it.
 * The cash deposits whose notes are in the drawer are said, so a count below
 * them is noticed before it is typed in for good. On the Cassa screen it is a
 * form of its own (`standalone`, UI-2e); inside a money form's drawer field it
 * is part of that form, whose own button it never presses: there it is no form.
 */
export function OpenDrawerForm({
  drawer,
  standalone = false,
  onOpened,
}: {
  drawer: CashDrawer
  standalone?: boolean
  /** Open now: `true` with this count, `false` opened meanwhile on another device. */
  onOpened?: (here: boolean) => void
}) {
  const { locale, messages: m } = useLocale()
  const { takeOver } = useAccess()
  const queryClient = useQueryClient()
  const titleId = useId()
  const reasonsId = useId()
  const [count, setCount] = useState('')
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const lastSent = useRef<{ body: string; key: string } | null>(null)
  const cents = parseEuroCents(count)
  const valid = cents !== null && cents <= MAX_COUNT_CENTS
  const held = drawer.depositsHeldCents
  const reasons = valid || busy ? [] : [m.cash.why.count]

  async function open() {
    if (cents === null || !valid || busy) return
    setProblem(null)
    const body = { locationId: drawer.location.id, countCents: cents }
    // A retry of the same opening replays; another count gets its own key.
    const serialized = JSON.stringify(body)
    if (lastSent.current?.body !== serialized) {
      lastSent.current = { body: serialized, key: newIdempotencyKey() }
    }
    setBusy(true)
    const result = await call(
      api.POST('/v1/staff/cash/sessions', {
        params: { header: { 'idempotency-key': lastSent.current.key } },
        body,
      }),
    )
    const openedElsewhere =
      !result.ok && result.problem.kind === 'api'
        ? result.problem.code === 'cash_drawer_open'
        : false
    if (result.ok || openedElsewhere) {
      // Opened here, or meanwhile on another device: either way it is open now. The button
      // stays busy, the count with it, until the drawer is read again as open (UI-2g): an
      // emptied count disabled the button the counter had pressed, and the focus went
      // with it. The key goes: replayed later, it would answer "opened" for a drawer
      // another device may have closed since.
      lastSent.current = null
      onOpened?.(result.ok)
      await queryClient.invalidateQueries({ queryKey: CASH_QUERY_KEY })
      setBusy(false)
      return
    }
    setBusy(false)
    if (!takeOver(result.problem)) setProblem(result.problem)
  }

  const fields: ReactNode = (
    <>
      <div className={FIELD_ROW}>
        <Field
          className={cn(FIELD, 'max-w-xs')}
          label={format(m.cash.openCount, { point: drawer.location.name })}
          hint={
            held > 0
              ? format(m.cash.depositsHeld, { amount: formatEuro(held, locale) })
              : m.cash.openCountHint
          }
          required
          error={count.trim() !== '' && !valid ? m.payment.amountInvalid : null}
        >
          {(control) => (
            <StaffInput
              {...control}
              inputMode="decimal"
              autoComplete="off"
              value={count}
              onChange={(event) => setCount(event.target.value)}
            />
          )}
        </Field>
      </div>
      {valid && cents !== null && openingShort(cents, held) ? (
        <Notice tone="warning">
          <p>{format(m.cash.openBelowDeposits, { amount: formatEuro(held, locale) })}</p>
        </Notice>
      ) : null}
      <ProblemNotice problem={problem} />
      <div>
        <Button
          type={standalone ? 'submit' : 'button'}
          variant="staff"
          busy={busy}
          disabled={reasons.length > 0}
          aria-describedby={reasons.length > 0 ? reasonsId : undefined}
          onClick={standalone ? undefined : () => void open()}
        >
          {m.cash.openSubmit}
          <NamedFor name={drawer.location.name} />
        </Button>
      </div>
      <ReasonList id={reasonsId} reasons={reasons} />
    </>
  )

  if (!standalone) return <div className="flex flex-col gap-3">{fields}</div>
  return (
    <form
      onSubmit={(event: FormEvent) => {
        event.preventDefault()
        void open()
      }}
      onKeyDown={noImplicitSubmit}
      aria-labelledby={titleId}
      noValidate
    >
      <SubPanel
        title={
          <span id={titleId}>
            {m.cash.openTitle}
            <NamedFor name={drawer.location.name} />
          </span>
        }
      >
        {fields}
      </SubPanel>
    </form>
  )
}
